import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { OtaReadiness } from '@/api/ota/types';
import { RequestOptions, axios, requestConfig } from '@/common/axios';
import { QueryConfig } from '@/common/react-query';
import { APIResponse } from '@/common/types';
import { useWavelink } from '@/components/providers/WavelinkProvider';
import { OTA_READINESS_QUERY_KEY, QUERIES_REFETCH_INTERVAL } from '@/config';
import { useOptionalSpotterContext } from '@/context/SpotterContext';

export type GetDeviceOtaReadinessResponse = APIResponse<OtaReadiness>;

const getDeviceOtaReadiness = async (
    { spotterId }: { spotterId: string },
    options?: RequestOptions
): Promise<OtaReadiness> => {
    const response = await axios.get<GetDeviceOtaReadinessResponse, GetDeviceOtaReadinessResponse>(
        `/user-rest/devices/${spotterId}/ota/readiness`,
        requestConfig(options)
    );

    return response.data;
};

export const deviceOtaReadinessQueryKey = (spotterId: string | null | undefined) => [
    OTA_READINESS_QUERY_KEY,
    spotterId,
];

export type UseDeviceOtaReadinessOptions = {
    spotterId?: string | null;
    config?: QueryConfig<typeof getDeviceOtaReadiness>;
};

/**
 * Reports whether a device can take an OTA update, and whether it is currently eligible for one.
 *
 * Takes `spotterId` from the argument, or from SpotterContext when the argument is omitted. Pass
 * `spotterId: null` rather than omitting it while an explicit id is still loading, so a surrounding
 * context is not picked up by accident. Undefined spotterId outside of a spotter context throws an error.
 */
export const useDeviceOtaReadiness = ({
    spotterId: spotterIdProp,
    config: { enabled = true, ...config } = {},
}: UseDeviceOtaReadinessOptions = {}) => {
    const { spotterId } = useOptionalSpotterContext({ spotterId: spotterIdProp });
    const { isQueryCovered } = useWavelink();

    const queryKey = useMemo(() => deviceOtaReadinessQueryKey(spotterId), [spotterId]);
    const isCoveredByWavelink = isQueryCovered(queryKey);

    return useQuery({
        queryKey,
        queryFn: ({ signal }) => getDeviceOtaReadiness({ spotterId: spotterId! }, { signal }),
        enabled: !!spotterId && enabled,
        staleTime: 1000 * 60 * 5, // 5 minutes
        refetchInterval: isCoveredByWavelink ? false : QUERIES_REFETCH_INTERVAL,
        ...config,
    });
};
